import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, ApiError, clearAuth, getAccessToken, saveAuth } from "./api";
import { loginEmail, loginGoogle, logoutFirebase, registerEmail, resetPassword } from "./firebase";
import type { AuthResponse, User } from "./types";

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (starCitizenId?: string, country?: string) => Promise<"ok" | "needs-profile">;
  completeGoogleProfile: (starCitizenId: string, country: string, password?: string) => Promise<AuthResponse>;
  finishGoogleProfile: (response: AuthResponse) => void;
  register: (email: string, password: string, starCitizenId: string, country: string) => Promise<void>;
  reset: (email: string) => Promise<void>;
  reloadProfile: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingGoogleToken, setPendingGoogleToken] = useState("");
  const [pendingGoogleProvider, setPendingGoogleProvider] = useState<"google" | "google-desktop">("google");

  const applyAuth = useCallback((response: AuthResponse) => {
    saveAuth(response);
    setUser(response.user);
    window.dispatchEvent(new Event("versecomms:login"));
  }, []);

  const reloadProfile = useCallback(async () => {
    if (!getAccessToken()) {
      setUser(null);
      return;
    }
    const profile = await api<User>("/me");
    setUser(profile);
  }, []);

  useEffect(() => {
    reloadProfile().catch(() => clearAuth()).finally(() => setLoading(false));
  }, [reloadProfile]);

  const exchangeFirebase = useCallback(async (idToken: string, provider: string, displayName?: string, country?: string) => {
    const response = await api<AuthResponse>("/auth/firebase", {
      method: "POST",
      anonymous: true,
      body: JSON.stringify({ idToken, provider, displayName, country })
    });
    applyAuth(response);
  }, [applyAuth]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    loginWithEmail: async (email, password) => {
      try {
        const token = await loginEmail(email.trim(), password);
        await exchangeFirebase(token, "password");
      } catch (error) {
        if (error instanceof Error && error.message.startsWith("Please verify your email first.")) {
          throw error;
        }
        const response = await api<AuthResponse>("/auth/login", {
          method: "POST",
          anonymous: true,
          body: JSON.stringify({ email: email.trim(), password })
        });
        applyAuth(response);
      }
    },
    loginWithGoogle: async (starCitizenId, country) => {
      const token = await loginGoogle();
      const provider = window.verseDesktop?.isDesktop ? "google-desktop" : "google";
      try {
        await exchangeFirebase(token, provider, starCitizenId, country);
        return "ok";
      } catch (error) {
        if (error instanceof ApiError && error.code === "google_registration_details_required") {
          setPendingGoogleToken(token);
          setPendingGoogleProvider(provider);
          return "needs-profile";
        }
        throw error;
      }
    },
    completeGoogleProfile: async (starCitizenId, country, password) => {
      if (!pendingGoogleToken) throw new Error("Google session expired. Please try again.");
      const response = await api<AuthResponse>("/auth/firebase", {
        method: "POST",
        anonymous: true,
        body: JSON.stringify({ idToken: pendingGoogleToken, provider: pendingGoogleProvider, displayName: starCitizenId, country, password: password || undefined })
      });
      saveAuth(response);
      setPendingGoogleToken("");
      setPendingGoogleProvider("google");
      return response;
    },
    finishGoogleProfile: response => setUser(response.user),
    register: async (email, password, starCitizenId) => {
      await registerEmail(email.trim(), password, starCitizenId.trim());
    },
    reset: resetPassword,
    reloadProfile,
    logout: async () => {
      clearAuth();
      setUser(null);
      window.dispatchEvent(new Event("versecomms:logout"));
      await logoutFirebase().catch(() => undefined);
    }
  }), [exchangeFirebase, loading, pendingGoogleProvider, pendingGoogleToken, reloadProfile, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
